/**
 * How long the transfer may make NO progress before it is abandoned.
 *
 * This is a stall timeout, not a duration budget: the clock restarts on every
 * chunk the browser reports, so a slow-but-healthy link finishes however long
 * it takes and only a transfer that has genuinely stopped moving is aborted.
 * A budget derived from an assumed line speed cannot tell *stalled* from
 * *slow* — it aborts a 20 MB document on a working 1 Mbit/s uplink, and
 * `UPLOAD_CONCURRENCY = 4` divides it again — which is why IMM-838 replaced
 * one with the other. Immune to link speed and to concurrency by construction.
 *
 * 30 s sits well past any retransmit or edge hiccup that recovers on its own,
 * and short enough that a member is not left watching a dead progress bar.
 */
export const UPLOAD_STALL_TIMEOUT_MS = 30_000;

/**
 * Deadline for the presign leg. A whole-call budget is right here and wrong for
 * the transfer: this is a small JSON round-trip with no body to speak of, so
 * there is no progress to observe and nothing a slow link can legitimately
 * spend 30 s on.
 */
export const PRESIGN_DEADLINE_MS = 30_000;

type StoragePresign = {
  readonly url: string;
  readonly fields: Readonly<Record<string, string>>;
};

/**
 * POSTs the file straight to object storage under a stall timeout.
 *
 * `XMLHttpRequest` rather than `fetch` for one reason: `fetch` reports nothing
 * about an upload in flight, so a bound built on it can only ever be a total
 * duration — the very thing this replaces. `xhr.upload`'s `progress` events are
 * the only browser-wide source of "bytes are still moving".
 *
 * A stall is not a rejection: without this the promise stays pending forever
 * and every in-flight flag behind it latches, leaving surfaces with no failure
 * path at all (BL-113).
 *
 * @param onFailure builds the rejection — each helper supplies its OWN error
 *   type, so a stall arrives as the failure that helper's callers already
 *   handle and the recovery costs nothing.
 */
export function boundedStoragePost(upload: StoragePresign, file: File, onFailure: () => Error): Promise<void> {
  const form = new FormData();

  // The `upload.fields` entries MUST be appended before the file; S3 rejects the
  // POST if the `file` part is not last.
  for (const [name, value] of Object.entries(upload.fields)) {
    form.append(name, value);
  }

  form.append('file', file);

  return new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest();
    let settled = false;
    let stall: ReturnType<typeof setTimeout> | undefined;

    const finish = (outcome: () => void): void => {
      if (settled) {
        return;
      }

      settled = true;
      clearTimeout(stall);
      outcome();
    };

    // Re-arms on every reported chunk, so the timer only ever fires against a
    // window in which nothing moved at all.
    const progressed = (): void => {
      if (settled) {
        return;
      }

      clearTimeout(stall);
      stall = setTimeout(() => {
        finish(() => {
          request.abort();
          reject(onFailure());
        });
      }, UPLOAD_STALL_TIMEOUT_MS);
    };

    request.upload.addEventListener('progress', progressed);
    // The response leg counts as movement too: a server that is answering has
    // not stalled, however long it takes to finish writing the object.
    request.addEventListener('progress', progressed);
    request.addEventListener('load', () => {
      finish(() => {
        if (request.status >= 200 && request.status < 300) {
          resolve();

          return;
        }

        reject(onFailure());
      });
    });
    request.addEventListener('error', () => {
      finish(() => reject(onFailure()));
    });
    request.addEventListener('abort', () => {
      finish(() => reject(onFailure()));
    });

    request.open('POST', upload.url);
    // Armed before `send`, so a connection that never opens — and therefore
    // never reports a first chunk — is bounded like any other stall.
    progressed();
    request.send(form);
  });
}

/**
 * Bounds the presign leg, which reaches the network through the generated
 * client and so takes no signal of its own.
 *
 * Deliberately NOT applied to `confirm`, though it is the same shape of call:
 * a presign that never settles has stored nothing, so abandoning it costs at
 * most an unconfirmed file record — the same thing any failed POST leaves
 * behind. A `confirm` that never settles may already have committed the item
 * server-side, so abandoning it shows the member a failure for an upload that
 * succeeded, and their retry creates a second copy. Confirm is a short server
 * call with nothing to stall on; let it complete (IMM-838).
 */
export async function boundedPresign<T>(request: Promise<T>, onFailure: () => Error): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(onFailure()), PRESIGN_DEADLINE_MS);
  });

  try {
    return await Promise.race([request, deadline]);
  } finally {
    clearTimeout(timer);
  }
}
